import { formatNumber } from '@angular/common';

const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';

export const debounceTimer = 300;

export class Utils {
  public static generateId = (length: number): string => {
    let result = '';
    for (var i = 0; i < length; i++) {
      result += characters.charAt(
        Math.floor(Math.random() * characters.length)
      );
    }
    return result;
  };

  public static isLeapYear(year: number) {
    return year % 100 === 0 ? year % 400 === 0 : year % 4 === 0;
  }

  public static getDaysInAYear(year: number) {
    return this.isLeapYear(year) ? 366 : 365;
  }

  public static annualAverageGrowthRate(
    from: number,
    to: number,
    time: number
  ) {
    if (from == 0 || to == 0 || time == 0) {
      return 0;
    }

    return (Math.pow(to / from, 1 / time) - 1) * 100;
  }

  public static formatNumber(
    n: number | undefined,
    convertUnits: boolean = false,
    format: string = '.0-1',
    locale: string = 'en-US'
  ): string {
    let unitObj: { unit: string; value: number };

    if (n === undefined) {
      return 'unavailable';
    } else if (n < 1000 || !convertUnits) {
      unitObj = { unit: '', value: n };
    } else if (n >= 1000 && n < 1000000) {
      unitObj = { unit: 'k', value: n / 1000 };
    } else if (n > 100000 && n < 1000000000) {
      unitObj = { unit: 'mn', value: n / 1000000 };
    } else if (n > 100000000 && n < 1000000000000) {
      unitObj = { unit: 'bn', value: n / 1000000000 };
    } else {
      unitObj = { unit: 'tn', value: n / 1000000000000 };
    }

    return `${formatNumber(unitObj.value, locale, format)}${unitObj.unit}`;
  }

  public static getColorHex(color: Color): string {
    return colorPalette[color.name][color.shade];
  }

  public static getColorRgb(color: Color): number[] {
    const colorHex = Utils.getColorHex(color);
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(colorHex);
    return result
      ? [
          parseInt(result[1], 16),
          parseInt(result[2], 16),
          parseInt(result[3], 16),
        ]
      : [];
  }

  public static interpolateColor(
    color1: number[],
    color2: number[],
    factor: number = 0.5
  ) {
    const result = color1.slice();
    for (let i = 0; i < 3; i++) {
      result[i] = Math.round(result[i] + factor * (color2[i] - color1[i]));
    }

    return result;
  }

  public static interpolateColors(color1: Color, color2: Color, steps: number) {
    const colorRgb1 = Utils.getColorRgb(color1);
    const colorRgb2 = Utils.getColorRgb(color2);
    const stepFactor = 1 / (steps === 1 ? 1 : steps - 1);
    const interpolatedColorArray: number[][] = [];

    for (let i = 0; i < steps; i++) {
      interpolatedColorArray.push(
        Utils.interpolateColor(colorRgb1, colorRgb2, stepFactor * i)
      );
    }

    return interpolatedColorArray;
  }
}

export enum ColorNames {
  Blue = 'blue',
  Green = 'green',
  Apricot = 'apricot',
  Red = 'red',
  Orange = 'orange',
  Gray = 'gray',
  Teal = 'teal',
  Purple = 'purple',
  Fuchsia = 'fuchsia',
}

export enum ColorShades {
  Shade0 = 0,
  Shade50 = 50,
  Shade100 = 100,
  Shade200 = 200,
  Shade300 = 300,
  Shade400 = 400,
  Shade500 = 500,
  Shade600 = 600,
  Shade700 = 700,
  Shade800 = 800,
}

export type Color = {
  name: ColorNames;
  shade: ColorShades;
};

export const colorPalette = {
  [ColorNames.Blue]: {
    [ColorShades.Shade0]: '#FFFFFF',
    [ColorShades.Shade100]: '#EEF4FF',
    [ColorShades.Shade200]: '#E3EBFF',
    [ColorShades.Shade300]: '#CEDCFE',
    [ColorShades.Shade400]: '#B5CBFF',
    [ColorShades.Shade500]: '#8AABFF',
    [ColorShades.Shade600]: '#375EC0',
    [ColorShades.Shade700]: '#29458C',
    [ColorShades.Shade800]: '#243647',
  },
  [ColorNames.Green]: {
    [ColorShades.Shade100]: '#EBFFF4',
    [ColorShades.Shade200]: '#A7D9CB',
    [ColorShades.Shade300]: '#A0C3B0',
    [ColorShades.Shade400]: '#5FB887',
    [ColorShades.Shade500]: '#28B869',
    [ColorShades.Shade600]: '#458461',
    [ColorShades.Shade700]: '#186B3E',
    [ColorShades.Shade800]: '#0C3820',
  },
  [ColorNames.Apricot]: {
    [ColorShades.Shade100]: '#FFEDCC',
    [ColorShades.Shade200]: '#FFE4B3',
    [ColorShades.Shade300]: '#F0CF90',
    [ColorShades.Shade400]: '#FFC966',
    [ColorShades.Shade500]: '#EDB347',
    [ColorShades.Shade600]: '#ED9A00',
    [ColorShades.Shade700]: '#A16900',
    [ColorShades.Shade800]: '#6E4700',
  },
  [ColorNames.Red]: {
    [ColorShades.Shade100]: '#FFE6E0',
    [ColorShades.Shade200]: '#FFBBAD',
    [ColorShades.Shade300]: '#FFA594',
    [ColorShades.Shade400]: '#E89492',
    [ColorShades.Shade500]: '#D78987',
    [ColorShades.Shade600]: '#CF4644',
    [ColorShades.Shade700]: '#9C3533',
    [ColorShades.Shade800]: '#692423',
  },
  [ColorNames.Orange]: {
    [ColorShades.Shade100]: '#FDEAE1',
    [ColorShades.Shade200]: '#FFCDB4',
    [ColorShades.Shade300]: '#FA915C',
    [ColorShades.Shade400]: '#EB6C2D',
    [ColorShades.Shade500]: '#CF4D0C',
    [ColorShades.Shade600]: '#9C3806',
    [ColorShades.Shade700]: '#692300',
    [ColorShades.Shade800]: '#3A1300',
  },
  [ColorNames.Gray]: {
    [ColorShades.Shade0]: '#FFFFFF',
    [ColorShades.Shade50]: '#F7F7F7',
    [ColorShades.Shade100]: '#E4E4E4',
    [ColorShades.Shade200]: '#C9C9C9',
    [ColorShades.Shade300]: '#ADADAD',
    [ColorShades.Shade400]: '#929292',
    [ColorShades.Shade500]: '#5C5C5C',
    [ColorShades.Shade600]: '#454545',
    [ColorShades.Shade700]: '#383838',
    [ColorShades.Shade800]: '#252525',
  },
  [ColorNames.Teal]: {
    [ColorShades.Shade100]: '#DAF0EC',
    [ColorShades.Shade200]: '#ACE3DA',
    [ColorShades.Shade300]: '#63BFB0',
    [ColorShades.Shade400]: '#329988',
    [ColorShades.Shade500]: '#0D806C',
    [ColorShades.Shade600]: '#146356',
    [ColorShades.Shade700]: '#0B483E',
    [ColorShades.Shade800]: '#042E27',
  },
  [ColorNames.Purple]: {
    [ColorShades.Shade100]: '#F8F7FF',
    [ColorShades.Shade200]: '#CBC8E6',
    [ColorShades.Shade300]: '#C0BDD9',
    [ColorShades.Shade400]: '#9B91D9',
    [ColorShades.Shade500]: '#887CD9',
    [ColorShades.Shade600]: '#58508D',
    [ColorShades.Shade700]: '#383359',
    [ColorShades.Shade800]: '#282440',
  },
  [ColorNames.Fuchsia]: {
    [ColorShades.Shade100]: '#FFEBF7',
    [ColorShades.Shade200]: '#FFD1ED',
    [ColorShades.Shade300]: '#FFB8E4',
    [ColorShades.Shade400]: '#F5B0D9',
    [ColorShades.Shade500]: '#F598D0',
    [ColorShades.Shade600]: '#A84680',
    [ColorShades.Shade700]: '#75315A',
    [ColorShades.Shade800]: '#421C33',
  },
};
